import React, { useState } from "react";
import Modal from "components/DesignSystem/Modal/Modal";
import { Button } from "components/DesignSystem/Button/Button";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { FormikForm } from "components/FormikForm/FormikForm";
import { svbConnectSchema } from "formValidations/svbConnectSchema";
import { FormikValues } from "formik";
import classNames from "classnames";
import { useAppSelector } from "hooks/useAppSelector";
import { useAppDispatch } from "hooks/useAppDispatch";
import { setSvbConnectModal } from "store/slices/svbConnect";
import { useConfirmConnectionMutation } from "store/apis/booksConnections";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import { ConditionalLink } from "components/conditionalLink";

type FormValues = {
  username: string;
  pin: string;
};

export const Step = ({
  step,
  children,
  isLast,
}: {
  step: number;
  children: React.ReactNode;
  isLast?: boolean;
}) => {
  return (
    <div
      className={classNames("t-h-max t-pl-2.5 t-relative", {
        "t-border t-border-solid t-border-purple-20 t-border-t-0 t-border-r-0 t-border-b-0":
          !isLast,
      })}
    >
      <div className="t-absolute t-flex t-left-0 t-items-center t-justify-center -t-translate-x-1/2 t-bg-surface t-h-5 t-w-5 t-text-neutral-20">
        <span className="t-inline-flex t-w-4 t-h-4 t-rounded-full t-items-center t-justify-center t-text-caption t-text-white t-bg-purple-40">
          {step}
        </span>
      </div>
      <div className="t-ml-3 t-space-y-1 t-pb-4">
        <div className="t-text-text-60 t-mb-0 t-text-body-sm" role="listitem">
          {children}
        </div>
      </div>
    </div>
  );
};

const SVBConnectModalContent = () => {
  const connectionId = useAppSelector((state) => state.svbConnect.connectionId);
  const entityId = useAppSelector((state) => state.svbConnect.entityId);
  const groupId = useAppSelector((state) => state.svbConnect.groupId);
  const codeOnlyMode = useAppSelector(
    (state) => state.svbConnect.code_only_mode
  );
  const txnPullChoice = useAppSelector(
    (state) => state.svbConnect.txn_pull_choice
  );
  const txnPullDate = useAppSelector((state) => state.svbConnect.txn_pull_date);

  const dispatch = useAppDispatch();

  const [step, setStep] = useState<"instructions" | "credentials">(
    "instructions"
  );
  const { successToast, alertToast } = useToast();
  const handleNext = () => setStep("credentials");

  const [confirmConnection, { isLoading: isConfirming }] =
    useConfirmConnectionMutation();

  const handleSubmit = async (values: FormikValues) => {
    if (codeOnlyMode) {
      return dispatch(
        setSvbConnectModal({
          isOpen: false,
          connectionId,
          entityId: undefined,
          groupId: undefined,
          txn_pull_choice: undefined,
          txn_pull_date: undefined,
          connection_successful: true,
          code: `${values.username} | ${values.pin}`,
          code_only_mode: true,
        })
      );
    }

    if (connectionId && groupId && entityId) {
      try {
        await confirmConnection({
          groupId,
          connectionId,
          entityId,
          code: `${values.username} | ${values.pin}`,
          txn_pull_date: txnPullDate,
          txn_pull_choice: txnPullChoice,
        }).unwrap();
        successToast({ message: "SVB account connected successfully!" });
        dispatch(
          setSvbConnectModal({
            isOpen: false,
            connectionId: undefined,
            entityId: undefined,
            groupId: undefined,
            txn_pull_choice: undefined,
            txn_pull_date: undefined,
            connection_successful: true,
            code_only_mode: false,
          })
        );
      } catch (error) {
        alertToast({
          message: (error as BackendError).data?.error?.message,
          title: "Connection Error",
        });
      }
    }
  };

  const initialValues: FormValues = {
    username: "",
    pin: "",
  };

  return (
    <FormikForm
      initialValues={initialValues}
      validationSchema={svbConnectSchema}
      onSubmit={handleSubmit}
    >
      {({ submitForm, isValid, dirty }) => (
        <>
          <Modal.Header>
            <Modal.Title>Connect Silicon Valley Bank - SVB</Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body>
            <div className="t-space-y-1">
              <p className="t-text-body-sm t-text-text-30 t-mb-4">
                Follow these simple steps to connect your SVB account
              </p>
              <Step step={1}>
                Log in to your{" "}
                <ConditionalLink
                  to="https://www.svb.com/"
                  className="!t-text-text-60 !t-underline"
                >
                  SVB Online Banking account.
                </ConditionalLink>{" "}
              </Step>
              <Step step={2}>
                In the left menu, go to{" "}
                <span className="t-text-subtext">
                  Integrations {">"} Direct Connect.
                </span>
              </Step>

              <Step step={3}>
                Click on{" "}
                <span className="t-text-subtext">"Generate New PIN"</span>- This
                will create a username and PIN - make sure to save them.
              </Step>
              <Step step={4} isLast>
                Return here and enter the{" "}
                <span className="t-text-subtext">
                  generated username and PIN
                </span>{" "}
                below to complete the connection.
              </Step>
            </div>

            <div className="t-ml-6">
              {step === "instructions" && (
                <Button
                  customType="primary_outlined"
                  onClick={handleNext}
                  size="small"
                >
                  Enter generated PIN
                </Button>
              )}

              {step === "credentials" && (
                <div className="t-space-y-4">
                  <TextInput
                    showErrorOnceTouched
                    label="Username"
                    required
                    name="username"
                  />
                  <TextInput
                    showErrorOnceTouched
                    name="pin"
                    label="PIN"
                    required
                  />
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.FooterButtonGroup>
            <Modal.RawClose asChild>
              <Button customType="secondary" size="small">
                Cancel
              </Button>
            </Modal.RawClose>
            <Button
              isLoading={isConfirming}
              customType="primary"
              size="small"
              onClick={submitForm}
              disabled={isConfirming || !isValid || !dirty}
            >
              Connect
            </Button>
          </Modal.FooterButtonGroup>
        </>
      )}
    </FormikForm>
  );
};

export const SVBConnectModal = () => {
  const isOpen = useAppSelector((state) => state.svbConnect.isOpen);

  const dispatch = useAppDispatch();
  const close = () =>
    dispatch(
      setSvbConnectModal({
        isOpen: false,
        connectionId: undefined,
        entityId: undefined,
        groupId: undefined,
        txn_pull_choice: undefined,
        txn_pull_date: undefined,
      })
    );

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content size="large">
        <SVBConnectModalContent />
      </Modal.Content>
    </Modal.Root>
  );
};
